import Typography from '@mui/material/Typography';
import { Card, CardActionArea, CardContent, Divider, Grid, useMediaQuery } from '@mui/material';
import React from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { PassType } from '../../api';
import { ProviderCapabilityGate } from '../../common/ui/ProviderCapabilityGate';
import { isRecurringPassType } from '../management/utils';
import { mb } from '../../common/theme/mbTokens';

type ChoosePassTypeProps = {
  onPick: (passType: PassType) => void;
};

const CARD_TYPES: PassType[] = [PassType.PUNCH_BASED, PassType.UNLIMITED_MONTHLY, PassType.FIXED_SCHEDULE_MONTHLY];

export const ChoosePassType = ({ onPick }: ChoosePassTypeProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  return (
    <Grid
      container
      flexDirection="column"
      alignItems="center"
      sx={{ flex: 1, minHeight: 0, width: '100%', p: mb.layout.cardPadding }}>
      <Typography variant={isSmallScreen ? 'h6' : 'h4'} sx={{ flexShrink: 0 }}>
        {t('passes.choose_pass_type_title')}
      </Typography>
      <ProviderCapabilityGate
        capability="recurring_passes"
        noteKey="paymentGate.membershipsUnsupported"
        taxLockedNoteKey="paymentGate.membershipsTaxLocked"
        sx={{ flex: 1, minHeight: 0, width: '100%', alignItems: 'center' }}>
        {(capabilityDisabled) => (
          <Grid
            container
            gap={2}
            justifyContent="center"
            alignItems="stretch"
            mt={isSmallScreen ? 3 : 4}
            sx={{ flex: 1, minHeight: 0, overflowY: 'auto', alignContent: 'flex-start' }}>
            {CARD_TYPES.map((passType) => {
              const locked = capabilityDisabled && isRecurringPassType(passType);
              return (
                <Card
                  key={passType}
                  sx={{
                    width: '280px',
                    maxWidth: '100%',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    border: '2px solid',
                    borderColor: locked ? 'divider' : 'primary.main',
                    opacity: locked ? 0.6 : 1,
                  }}>
                  <CardActionArea
                    disabled={locked}
                    onClick={() => onPick(passType)}
                    sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Grid container flexDirection="column" gap={1.5} sx={{ flexGrow: 1 }}>
                        <Typography variant={isSmallScreen ? 'subtitle1' : 'h5'}>
                          {t(`passes.pass_type.${passType}`)}
                        </Typography>
                        <Divider sx={{ borderColor: locked ? 'divider' : 'primary.main' }} />
                        {(['what', 'billing', 'example'] as const).map((field) => (
                          <Grid key={field} container flexDirection="column">
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              color="text.secondary"
                              sx={{ lineHeight: 1.2 }}>
                              {t(`passes.pass_type_description.${field}_label`)}
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              fontWeight={isSmallScreen ? 300 : 400}
                              sx={{ lineHeight: 1.3 }}>
                              {t(`passes.pass_type_description.${passType}.${field}`)}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Grid>
        )}
      </ProviderCapabilityGate>
    </Grid>
  );
};
